import { defaultLayer } from '@strength/db-schema';
import type { WorkoutLayer } from '@strength/db-schema';
import { DATE_COLUMNS } from './date-columns.gen';
import type { DateColumns } from './date-columns.gen';
import { schema as zeroSchema } from './zero-schema.gen';
import type { Schema } from './zero-schema.gen';

/**
 * Normalizes Zero read output to the tRPC shape for the same row:
 * - date columns (`date` in the Drizzle schema): epoch ms -> `YYYY-MM-DD`
 *   (UTC — never local time, so the value matches what the DB stores).
 * - timestamp columns (`timestamp with time zone`): epoch ms -> ISO string.
 * - `layer` column: filled with WorkoutLayer defaults (existing behavior).
 * - related rows (`.related(...)` results) are normalized recursively via the
 *   generated relationships.
 *
 * Null, absent and non-finite values (PG `'infinity'` parses to `Infinity`,
 * which `Date.toISOString()` would throw on) are preserved exactly.
 * Hot-path per-row transform: native `Date` only, per-table plans memoized.
 */

type ColumnKind = 'date' | 'timestamp';

/**
 * Largest epoch ms `Date` can represent (±100,000,000 days from epoch).
 * `toISOString()` throws RangeError outside this window, so values beyond it
 * are preserved instead of converted — see the never-throw contract below.
 */
const MAX_ISO_EPOCH_MS = 8.64e15;

interface TablePlan {
  dateColumns: string[];
  timestampColumns: string[];
  related: Array<{ name: string; table: string }>;
}

// date/timestamp columns and relationship destinations per table, derived
// from the generated literals with typed iteration — no casts. Populated once
// at module load; lookups in buildPlan are O(1) Map gets.
const columnPlans = new Map<
  string,
  {
    dateColumns: Array<string>;
    timestampColumns: Array<string>;
  }
>();

for (const [table, columns] of Object.entries(DATE_COLUMNS)) {
  const entry: { dateColumns: string[]; timestampColumns: string[] } = {
    dateColumns: [],
    timestampColumns: [],
  };
  for (const [name, kind] of Object.entries(columns)) {
    (kind === 'date' ? entry.dateColumns : entry.timestampColumns).push(name);
  }
  columnPlans.set(table, entry);
}

const relatedPlans = new Map<string, Array<{ name: string; table: string }>>();

for (const [table, rels] of Object.entries(zeroSchema.relationships)) {
  const entry: Array<{ name: string; table: string }> = [];
  for (const [name, targets] of Object.entries(rels)) {
    const dest = targets[0]?.destSchema;
    if (dest) entry.push({ name, table: dest });
  }
  relatedPlans.set(table, entry);
}

function buildPlan(table: string | undefined): TablePlan {
  const columns = table ? columnPlans.get(table) : undefined;
  const rels = table ? relatedPlans.get(table) : undefined;
  return {
    dateColumns: columns?.dateColumns ?? [],
    timestampColumns: columns?.timestampColumns ?? [],
    related: rels ?? [],
  };
}

// Fallback for rows whose table is unknown (e.g. the mocked query objects in
// tests): every date/timestamp column name across all synced tables, so the
// transform still matches the tRPC shape. Column names are globally unique
// across the synced tables today; on a collision the first kind wins.
function buildFallbackPlan(): TablePlan {
  const plan: TablePlan = {
    dateColumns: [],
    timestampColumns: [],
    related: [],
  };
  for (const entry of columnPlans.values()) {
    for (const name of entry.dateColumns) {
      if (!plan.dateColumns.includes(name)) plan.dateColumns.push(name);
    }
    for (const name of entry.timestampColumns) {
      if (!plan.timestampColumns.includes(name)) {
        plan.timestampColumns.push(name);
      }
    }
  }
  return plan;
}

const planCache = new Map<string, TablePlan>();

function planFor(table: string | undefined): TablePlan {
  const key = table ?? '';
  let plan = planCache.get(key);
  if (!plan) {
    plan = table ? buildPlan(table) : buildFallbackPlan();
    planCache.set(key, plan);
  }
  return plan;
}

function toIsoString(ms: number, kind: ColumnKind): string {
  const iso = new Date(ms).toISOString();
  return kind === 'date' ? iso.slice(0, 10) : iso;
}

/**
 * The root table a Zero query reads from, when it can be determined.
 * Accepts every shape a read funnel can receive:
 * - legacy query requests and the new Query class (`.table` / `.ast.table`),
 * - named-query requests (`queries.foo(...)`, a `{args, query}` QueryRequest)
 *   — the definition is re-derived against a permissive context purely to
 *   observe its root table, since definitions are pure query-builder chains.
 * Mocked/plain query objects (tests) may carry neither shape.
 */
export function queryTableName(query: unknown): string | undefined {
  // SAFETY: introspecting Zero's query objects, which do not expose the root
  // table in their public types.
  const q = query as {
    table?: unknown;
    ast?: { table?: unknown };
    query?: unknown;
    args?: unknown;
  } | null;
  if (q == null) return undefined;
  if (typeof q.table === 'string') return q.table;
  if (q.ast != null && typeof q.ast.table === 'string') return q.ast.table;
  if (q.query != null) return tableOfQueryRequest(q);
  return undefined;
}

// A permissive value: every property access, call and construction yields
// another permissive value. Used only to walk a named query definition far
// enough to observe which table its chain starts from.
// Opaque on purpose — only ever passed into query definitions, never read.
type PermissiveValue = object;

function permissiveValue(): PermissiveValue {
  let proxy: PermissiveValue;
  const fn = (..._args: unknown[]): PermissiveValue => proxy;
  proxy = new Proxy(fn, {
    get: (_target, prop) => {
      if (prop === Symbol.toPrimitive) return () => '';
      if (prop === 'toString' || prop === 'valueOf') return () => '';
      if (prop === Symbol.iterator) {
        return function* () {
          yield proxy;
        };
      }
      return proxy;
    },
    apply: () => proxy,
    construct: () => proxy,
    has: () => true,
  });
  return proxy;
}

// The root table of a named query definition is fixed at registration time,
// so the derivation result is cached per definition function. A failed
// derivation is cached as undefined and the definition permanently uses the
// union fallback plan (valid in practice: Zero rejects malformed args before
// the real read path reaches here).
const queryRequestTableCache = new WeakMap<object, string | undefined>();

function tableOfQueryRequest(request: {
  query?: unknown;
  args?: unknown;
}): string | undefined {
  // SAFETY: the named query object carries `fn` and stable identity but no
  // public typing for either.
  const namedQuery = request.query as
    | {
        fn?: (o: { ctx: unknown; args: unknown }) => unknown;
      }
    | undefined;
  if (typeof namedQuery?.fn !== 'function') return undefined;
  const cached = queryRequestTableCache.get(namedQuery);
  if (queryRequestTableCache.has(namedQuery)) return cached;
  let table: string | undefined;
  try {
    // SAFETY: re-derives the root Query with a permissive context; only the
    // AST's table name is read and the derived query is discarded. A
    // definition that needs a real session throws and falls back to undefined.
    const derived = namedQuery.fn({
      ctx: permissiveValue(),
      args: request.args,
    });
    const ast = (derived as { ast?: { table?: unknown } } | undefined)?.ast;
    if (ast != null && typeof ast.table === 'string') table = ast.table;
  } catch {
    table = undefined;
  }
  queryRequestTableCache.set(namedQuery, table);
  return table;
}

export function normalizeZeroRow<TRow>(row: TRow, table?: string): TRow {
  if (row == null || typeof row !== 'object') return row;
  const plan = planFor(table);
  // SAFETY: shallow-copy transform of a plain Zero row; the shape is preserved.
  const result = { ...(row as Record<string, unknown>) };

  const setColumn = (
    name: string,
    kind: ColumnKind,
    row: Record<string, unknown>,
  ) => {
    const value = row[name];
    // Non-finite or out-of-ISO-range epoch values (PG 'infinity' -> Infinity,
    // invalid Date -> NaN, corrupt epochs) and null/undefined pass through
    // untouched: converting them would throw. Edge values therefore surface
    // numerically even though NormalizedRow claims string.
    if (value instanceof Date) {
      const ms = value.getTime();
      if (Number.isFinite(ms) && Math.abs(ms) <= MAX_ISO_EPOCH_MS) {
        row[name] = toIsoString(ms, kind);
      }
      return;
    }
    if (
      typeof value === 'number' &&
      Number.isFinite(value) &&
      Math.abs(value) <= MAX_ISO_EPOCH_MS
    ) {
      row[name] = toIsoString(value, kind);
    }
  };

  for (const name of plan.dateColumns) {
    if (name in result) setColumn(name, 'date', result);
  }
  for (const name of plan.timestampColumns) {
    if (name in result) setColumn(name, 'timestamp', result);
  }
  if ('layer' in result) {
    result['layer'] = defaultLayer(
      (result['layer'] ?? {}) as Partial<WorkoutLayer>,
    );
  }
  for (const rel of plan.related) {
    if (!(rel.name in result)) continue;
    const related = result[rel.name];
    if (Array.isArray(related)) {
      result[rel.name] = related.map((r) => normalizeZeroRow(r, rel.table));
    } else if (related != null && typeof related === 'object') {
      result[rel.name] = normalizeZeroRow(related, rel.table);
    }
  }

  return result as TRow;
}

// ---------------------------------------------------------------------------
// Type-level remap: outputs are typed asymmetrically. Query inputs (e.g.
// `.where('startDate', '>=', epochMs)`) stay epoch-ms numbers — zero-cache
// compares date columns as numbers on the wire — while read outputs type as
// the tRPC shape (date/timestamp columns as strings).
// ---------------------------------------------------------------------------

type DateColumnNames<TTable extends string> = TTable extends keyof DateColumns
  ? keyof DateColumns[TTable] & string
  : never;

// table -> relationship name -> destination table name, resolved concretely per
// table so the generated relationship entries can be read at definition time.
type RelationshipTargets = {
  [TTable in keyof Schema['relationships'] & string]: {
    [TRel in keyof Schema['relationships'][TTable] &
      string]: Schema['relationships'][TTable][TRel] extends readonly (infer TEntry)[]
      ? TEntry extends { destSchema: string }
        ? TEntry['destSchema']
        : never
      : never;
  };
};

type RelationshipNames<TTable extends string> =
  TTable extends keyof RelationshipTargets
    ? keyof RelationshipTargets[TTable] & string
    : never;

type RelationshipTarget<
  TTable extends string,
  TRelationship extends string,
> = TTable extends keyof RelationshipTargets
  ? TRelationship extends keyof RelationshipTargets[TTable]
    ? RelationshipTargets[TTable][TRelationship]
    : never
  : never;

type NormalizedColumn<TValue> = null extends TValue ? string | null : string;

/**
 * Maps a Zero row type to its normalized (tRPC-shaped) form: date/timestamp
 * columns become strings, related rows are remapped recursively via the
 * generated relationships. Unknown tables pass through unchanged except for
 * their own known relationships.
 */
export type NormalizedRow<
  TTable extends string,
  TRow,
> = TRow extends readonly (infer TItem)[]
  ? readonly NormalizedRow<TTable, TItem>[]
  : TRow extends null | undefined
    ? TRow
    : {
        [K in keyof TRow]: K extends DateColumnNames<TTable>
          ? NormalizedColumn<TRow[K]>
          : K extends RelationshipNames<TTable>
            ? NormalizedRow<RelationshipTarget<TTable, K & string>, TRow[K]>
            : TRow[K];
      };
